import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { toast } from "react-toastify";
import useAxiosClient from "../axios-client";
import "./TeamSettings.css";

const MAX_VISIBLE_AVATARS = 4;
const UNASSIGNED = "unassigned";

const initialsOf = (m) =>
  `${(m.firstname || "").charAt(0)}${(m.lastname || "").charAt(0)}`.toUpperCase();
const fullName = (m) => `${m.firstname} ${m.lastname}`.trim();
const isUnassigned = (t) => (t.name || "").trim().toLowerCase() === UNASSIGNED;

/** SUDO → SUDO, Admin → MANAGER, anything else → USER. */
const roleLabel = (role) =>
  !role ? "USER" : role === "SUDO" ? "SUDO" : role.toLowerCase() === "admin" ? "MANAGER" : role.toUpperCase();

const TeamSettings = () => {
  const axiosClient = useAxiosClient();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const filterRef = useRef(null);

  // -- inline create -----------------------------------------------------------
  const [creating, setCreating] = useState(false);
  const [createValue, setCreateValue] = useState("");
  const [createError, setCreateError] = useState(null);
  const [savingCreate, setSavingCreate] = useState(false);
  const createInputRef = useRef(null);

  // -- inline rename -----------------------------------------------------------
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameError, setRenameError] = useState(null);
  const [savingRename, setSavingRename] = useState(false);
  const renameInputRef = useRef(null);

  // -- delete confirm ----------------------------------------------------------
  const [deletingId, setDeletingId] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [savingDelete, setSavingDelete] = useState(false);

  // -- membership slide-over ---------------------------------------------------
  const [managingId, setManagingId] = useState(null);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [teamMembers, setTeamMembers] = useState({}); // teamId → members[]
  const [addTab, setAddTab] = useState("everyone"); // 'everyone' | 'unassigned'
  const [addSearch, setAddSearch] = useState("");
  const [moveMenuFor, setMoveMenuFor] = useState(null); // userId whose move menu is open
  const [pendingMoves, setPendingMoves] = useState([]); // { userId, userName, fromTeamId, fromName, toTeamId, toName }
  const [savingMembers, setSavingMembers] = useState(false);

  // -- data -------------------------------------------------------------------
  const fetchTeams = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosClient.get(`/settings/teams/index`);
      const data = response.data.data || [];
      const sorted = [...data].sort((a, b) => {
        const aUn = isUnassigned(a);
        const bUn = isUnassigned(b);
        if (aUn && !bUn) return -1;
        if (!aUn && bUn) return 1;
        return a.name.localeCompare(b.name);
      });
      setTeams(sorted);

      // Prime member lists so every card shows its avatars immediately.
      const memberMap = {};
      await Promise.all(
        sorted.map(async (team) => {
          try {
            const mRes = await axiosClient.get(`/settings/teams/${team.id}/members`);
            memberMap[team.id] = mRes.data.data || [];
          } catch {
            memberMap[team.id] = [];
          }
        })
      );
      setTeamMembers(memberMap);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  }, [axiosClient]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  // -- focus inputs ------------------------------------------------------------
  useEffect(() => {
    if (creating && createInputRef.current) createInputRef.current.focus();
  }, [creating]);
  useEffect(() => {
    if (renamingId && renameInputRef.current) renameInputRef.current.focus();
  }, [renamingId]);

  // -- global filter: "/" focuses the box --------------------------------------
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && !creating && !renamingId && !managingId) {
        const tag = document.activeElement?.tagName;
        if (tag !== "INPUT" && tag !== "TEXTAREA") {
          e.preventDefault();
          filterRef.current?.focus();
        }
      }
      if (e.key === "Escape") {
        if (managingId) setManagingId(null);
        else if (creating) cancelCreate();
        else if (renamingId) cancelRename();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [creating, renamingId, managingId]);

  // -- duplicate check ---------------------------------------------------------
  const isDuplicate = (value, excludeId = null) => {
    const norm = value.trim().toLowerCase();
    return teams.some(
      (t) => t.name.trim().toLowerCase() === norm && t.id !== excludeId
    );
  };

  // -- create handlers ---------------------------------------------------------
  const handleOpenCreate = () => {
    setCreating(true);
    setCreateValue("");
    setCreateError(null);
  };
  const cancelCreate = () => {
    setCreating(false);
    setCreateValue("");
    setCreateError(null);
  };
  const handleCreateChange = (e) => {
    const val = e.target.value;
    setCreateValue(val);
    setCreateError(
      val.trim() && isDuplicate(val)
        ? `A team called "${teams.find((t) => t.name.trim().toLowerCase() === val.trim().toLowerCase())?.name}" already exists.`
        : null
    );
  };
  const handleCreateSubmit = async () => {
    const name = createValue.trim();
    if (!name || isDuplicate(name)) return;
    setSavingCreate(true);
    try {
      const res = await axiosClient.post(`/settings/teams/store`, { name });
      toast.success(res.data.message);
      await fetchTeams();
      cancelCreate();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to create team");
    } finally {
      setSavingCreate(false);
    }
  };
  const handleCreateKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCreateSubmit();
    } else if (e.key === "Escape") {
      cancelCreate();
    }
  };

  // -- rename handlers ---------------------------------------------------------
  const handleOpenRename = (team) => {
    setRenamingId(team.id);
    setRenameValue(team.name);
    setRenameError(null);
  };
  const cancelRename = () => {
    setRenamingId(null);
    setRenameValue("");
    setRenameError(null);
  };
  const handleRenameChange = (e) => {
    const val = e.target.value;
    setRenameValue(val);
    setRenameError(
      val.trim() && isDuplicate(val, renamingId)
        ? `A team called "${teams.find((t) => t.name.trim().toLowerCase() === val.trim().toLowerCase())?.name}" already exists.`
        : null
    );
  };
  const handleRenameConfirm = async () => {
    const name = renameValue.trim();
    if (!name || name === teams.find((t) => t.id === renamingId)?.name) {
      cancelRename();
      return;
    }
    if (isDuplicate(name, renamingId)) return;
    setSavingRename(true);
    try {
      const res = await axiosClient.put(`/settings/teams/update/${renamingId}`, { name });
      toast.success(res.data.message);
      await fetchTeams();
      cancelRename();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to update";
      toast.error(msg);
      setRenameError(msg);
    } finally {
      setSavingRename(false);
    }
  };
  const handleRenameKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleRenameConfirm();
    } else if (e.key === "Escape") {
      cancelRename();
    }
  };

  // -- delete handlers ---------------------------------------------------------
  const handleOpenDelete = (id) => {
    setDeletingId(id);
    setDeleteError(null);
  };
  const handleDeleteConfirm = async (team) => {
    setSavingDelete(true);
    try {
      const res = await axiosClient.delete(`/settings/teams/delete/${team.id}`);
      toast.success(res.data.message);
      setDeletingId(null);
      setDeleteError(null);
      await fetchTeams();
    } catch (err) {
      setDeleteError(err.response?.data?.message || err.message || "Failed to delete");
    } finally {
      setSavingDelete(false);
    }
  };

  // -- membership slide-over ---------------------------------------------------
  const openManage = async (team) => {
    setManagingId(team.id);
    setAddTab("everyone");
    setAddSearch("");
    setMoveMenuFor(null);
    setPendingMoves([]);
    try {
      const res = await axiosClient.get(`/settings/teams/assignableUsers`);
      setAssignableUsers(res.data.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    }
  };

  const managingTeam = useMemo(
    () => teams.find((t) => t.id === managingId) || null,
    [teams, managingId]
  );
  const members = useMemo(
    () => (managingId ? teamMembers[managingId] || [] : []),
    [managingId, teamMembers]
  );

  const upsertMove = (move) => {
    setPendingMoves((prev) => {
      const rest = prev.filter((m) => m.userId !== move.userId);
      return [...rest, move];
    });
    setMoveMenuFor(null);
  };
  const removeMove = (userId) => {
    setPendingMoves((prev) => prev.filter((m) => m.userId !== userId));
  };
  const moveFor = (userId) => pendingMoves.find((m) => m.userId === userId);

  // Move a current member to another team (or unassigned).
  const handleMoveMember = (member, targetTeam) => {
    if (!managingTeam || targetTeam.id === managingTeam.id) return;
    upsertMove({
      userId: member.id,
      userName: fullName(member),
      fromTeamId: managingTeam.id,
      fromName: managingTeam.name,
      toTeamId: targetTeam.id,
      toName: targetTeam.name,
    });
  };

  // Add a person to this team (they leave their current team).
  const toggleAddPerson = (user) => {
    if (!managingTeam) return;
    const already = members.some((m) => m.id === user.id);
    if (already) return;
    const existing = moveFor(user.id);
    if (existing) {
      removeMove(user.id);
      return;
    }
    const fromTeam = teams.find((t) => t.id === user.team_id);
    upsertMove({
      userId: user.id,
      userName: fullName(user),
      fromTeamId: user.team_id || null,
      fromName: fromTeam ? fromTeam.name : "Unassigned",
      toTeamId: managingTeam.id,
      toName: managingTeam.name,
    });
  };

  const saveMembers = async () => {
    if (pendingMoves.length === 0 || !managingTeam) return;
    setSavingMembers(true);
    try {
      // Group pending moves by destination team, then assign in one call each.
      const byTeam = {};
      pendingMoves.forEach((m) => {
        (byTeam[m.toTeamId] = byTeam[m.toTeamId] || []).push(m.userId);
      });
      for (const [teamId, userIds] of Object.entries(byTeam)) {
        await axiosClient.post(`/settings/teams/${teamId}/assignUsers`, {
          user_ids: userIds,
        });
      }
      toast.success(`Moved ${pendingMoves.length} member${pendingMoves.length !== 1 ? "s" : ""}`);
      await fetchTeams();
      setManagingId(null);
      setPendingMoves([]);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setSavingMembers(false);
    }
  };

  // -- filtering ---------------------------------------------------------------
  const filteredTeams = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return teams;
    return teams.filter((t) => {
      if (t.name.toLowerCase().includes(q)) return true;
      const ms = teamMembers[t.id] || [];
      return ms.some((m) => fullName(m).toLowerCase().includes(q));
    });
  }, [teams, teamMembers, filter]);

  // People available to add, filtered by tab + search.
  const addablePeople = useMemo(() => {
    const alreadyIds = new Set(members.map((m) => m.id));
    let list = assignableUsers.filter((u) => !alreadyIds.has(u.id));
    if (addTab === "unassigned") {
      list = list.filter((u) => !u.team_id || isUnassigned(teams.find((t) => t.id === u.team_id) || {}));
    }
    if (addSearch.trim()) {
      const q = addSearch.trim().toLowerCase();
      list = list.filter((u) => fullName(u).toLowerCase().includes(q));
    }
    return list;
  }, [assignableUsers, members, addTab, addSearch, teams]);

  const unassignedTeam = teams.find(isUnassigned);
  const unassignedCount = unassignedTeam?.userCount ?? 0;
  const totalMembers = teams.reduce((sum, t) => sum + (t.userCount || 0), 0);

  const renderTeamCard = (team) => {
    const un = isUnassigned(team);
    const isRenaming = renamingId === team.id;
    const isDeleting = deletingId === team.id;
    const ms = teamMembers[team.id] || [];
    const visible = ms.slice(0, MAX_VISIBLE_AVATARS);
    const hidden = ms.length - MAX_VISIBLE_AVATARS;

    return (
      <div key={team.id} className={`ts-card ${un ? "ts-card--unassigned" : ""} ${isRenaming ? "ts-card--active" : ""}`}>
        {isRenaming ? (
          <div className="ts-inline-input">
            <input
              ref={renameInputRef}
              value={renameValue}
              onChange={handleRenameChange}
              onKeyDown={handleRenameKeyDown}
            />
            <span className="ts-caret" />
            {savingRename && <span className="ts-input-err-icon">…</span>}
          </div>
        ) : (
          <div className="ts-card-head">
            <div className="ts-card-title-wrap">
              <div className="ts-card-title-row">
                <span className="ts-card-title">{team.name}</span>
                {un && <span className="ts-system-badge">SYSTEM</span>}
              </div>
              {un ? (
                <div className="ts-card-desc">People not yet placed on a team. Can't be renamed or deleted.</div>
              ) : (
                <div className="ts-card-meta">
                  <b>{team.userCount ?? 0}</b> members
                  <span>·</span>
                  <b>{team.ordersCount ?? 0}</b> orders
                  {team.createdAt && (
                    <>
                      <span>·</span>
                      <span>since {team.createdAt}</span>
                    </>
                  )}
                </div>
              )}
            </div>
            {un ? (
              <span className="ts-card-dash">—</span>
            ) : (
              <div className="ts-card-actions">
                <button className="ts-icon-btn" title="Rename" onClick={() => handleOpenRename(team)}>
                  ✎
                </button>
                <button
                  className={`ts-icon-btn ts-icon-btn--delete ${isDeleting ? "ts-icon-btn--delete" : ""}`}
                  title="Delete"
                  onClick={() => handleOpenDelete(team.id)}
                >
                  🗑
                </button>
              </div>
            )}
          </div>
        )}

        {/* rename footer hints */}
        {isRenaming && (
          <div className="ts-card-foot">
            <span className="ts-card-foot-hint">
              {renameError || `${ms.length} member${ms.length !== 1 ? "s" : ""} · ${team.ordersCount ?? 0} orders`}
            </span>
            <div className="ts-kbd-group">
              <button type="button" className="ts-kbd ts-kbd-btn" onClick={cancelRename}>
                Esc
              </button>
              <button type="button" className="ts-kbd ts-kbd--accent ts-kbd-btn" onClick={handleRenameConfirm}>
                {savingRename ? "…" : "↵ Save"}
              </button>
            </div>
          </div>
        )}

        {!isRenaming && ms.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div className="ts-avatars">
              {visible.map((m, i) => (
                <span
                  key={m.id}
                  className={`ts-avatar ${!un && i === 0 ? "ts-avatar--accent" : ""}`}
                  title={fullName(m)}
                >
                  {initialsOf(m)}
                </span>
              ))}
              {hidden > 0 && <span className="ts-avatar ts-avatar--more">+{hidden}</span>}
            </div>
            <span
              style={{
                font: "400 12px Outfit, sans-serif",
                color: "var(--muted)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
                minWidth: 0,
              }}
            >
              {un
                ? ms.map(fullName).join(", ")
                : `${ms.slice(0, 3).map((m) => m.firstname).join(", ")}${ms.length > 3 ? ` +${ms.length - 3}` : ""}`}
            </span>
          </div>
        )}

        {/* card footer */}
        {!isRenaming && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              paddingTop: 11,
              borderTop: "1px solid var(--border)",
            }}
          >
            {un ? (
              <span style={{ font: "500 11.5px var(--ts-mono)", color: "var(--ts-warn)" }}>
                {ms.length} waiting to be placed
              </span>
            ) : (
              <span
                style={{
                  font: "500 11.5px Outfit, sans-serif",
                  color: "var(--dim)",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => (e.target.style.color = "var(--text)")}
                onMouseLeave={(e) => (e.target.style.color = "var(--dim)")}
              >
                Team dashboard ↗
              </span>
            )}
            <button className={un ? "ts-btn-tint" : "ts-btn-ghost"} style={{ height: 30, padding: "0 12px" }} onClick={() => openManage(team)}>
              {un ? "Place people →" : "Manage members"}
            </button>
          </div>
        )}

        {/* inline delete confirm */}
        {isDeleting && (
          <div className="ts-delete-confirm">
            <div className="ts-delete-question">
              Delete <strong>{team.name}</strong> permanently?{" "}
              {team.userCount > 0
                ? "Reassign its members to another team first."
                : "This cannot be undone."}
            </div>
            {deleteError && <div className="ts-delete-error">✕ {deleteError}</div>}
            <div className="ts-delete-actions">
              <button className="ts-btn-ghost" onClick={() => { setDeletingId(null); setDeleteError(null); }}>
                Cancel
              </button>
              <button
                className="ts-btn-danger"
                onClick={() => handleDeleteConfirm(team)}
                disabled={team.userCount > 0 || savingDelete}
              >
                Delete
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="ts-page">
      {/* ---- header ---- */}
      <div className="ts-header">
        <div className="ts-header-left">
          <h1>Team Settings</h1>
          <p>
            Teams scope orders and dashboards. Everyone belongs to exactly one
            team — adding someone here moves them out of theirs.
          </p>
        </div>
        <div className="ts-header-right">
          <div className="ts-filter">
            <span className="ts-filter-icon">⌕</span>
            <input
              ref={filterRef}
              placeholder="Filter teams or people"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            {filter ? (
              <span
                className="ts-filter-icon"
                style={{ cursor: "pointer" }}
                onClick={() => setFilter("")}
              >
                ✕
              </span>
            ) : (
              <span className="ts-filter-shortcut">/</span>
            )}
          </div>
          <div className="ts-stats">
            <b>{teams.length}</b> teams
            <span className="ts-stats-sep">·</span>
            <b>{totalMembers}</b> people
            <span className="ts-stats-sep">·</span>
            <b className="ts-stats-warn">{unassignedCount}</b> unassigned
          </div>
          <button className="ts-btn-primary" onClick={handleOpenCreate}>
            + New Team
          </button>
        </div>
      </div>

      {/* ---- grid ---- */}
      <div className="ts-grid">
        {/* inline create card */}
        {creating && (
          <div className={`ts-card ts-card--active ${createError ? "" : ""}`}>
            <div className={`ts-card-label ${createError ? "ts-card-label--err" : ""}`}>New team</div>
            <div className={`ts-inline-input ${createError ? "ts-inline-input--err" : ""}`}>
              <input
                ref={createInputRef}
                placeholder="Team name…"
                value={createValue}
                onChange={handleCreateChange}
                onKeyDown={handleCreateKeyDown}
              />
              <span className="ts-caret" />
              {createError && <span className="ts-input-err-icon">!</span>}
            </div>
            <div className="ts-card-foot">
              <span className={`ts-card-foot-hint ${createError ? "ts-card-foot-hint--err" : ""}`}>
                {createError || "Starts empty — add people after."}
              </span>
              <div className="ts-kbd-group">
                <button type="button" className="ts-kbd ts-kbd-btn" onClick={cancelCreate}>
                  Esc
                </button>
                <button type="button" className="ts-kbd ts-kbd--accent ts-kbd-btn" onClick={handleCreateSubmit}>
                  {savingCreate ? "…" : "↵ Create"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* skeleton */}
        {loading &&
          [0, 1, 2].map((i) => (
            <div key={i} className="ts-skeleton" style={{ animationDelay: `${i * 0.15}s` }}>
              <div className="ts-skeleton-line" style={{ width: `${60 - i * 12}%` }} />
              <div className="ts-skeleton-line" style={{ width: `${44 - i * 8}%` }} />
              <div className="ts-avatars">
                <span className="ts-avatar" />
                <span className="ts-avatar" />
                <span className="ts-avatar" />
              </div>
              <div className="ts-skeleton-line" style={{ width: "40%" }} />
            </div>
          ))}

        {/* no matches */}
        {!loading && filter && filteredTeams.length === 0 && (
          <div className="ts-empty">
            <div className="ts-empty-title">No team or person matches “{filter}”</div>
            <div className="ts-empty-sub">
              Searched {teams.length} team names and {totalMembers} staff names.
            </div>
            <div className="ts-empty-actions">
              <button className="ts-btn-ghost" onClick={() => setFilter("")}>
                Clear filter
              </button>
              <button
                className="ts-btn-primary"
                onClick={() => {
                  setCreateValue(filter);
                  setCreating(true);
                  setFilter("");
                }}
              >
                + New team “{filter}”
              </button>
            </div>
          </div>
        )}

        {/* empty state (only unassigned exists) */}
        {!loading && !filter && teams.length === 1 && isUnassigned(teams[0]) && (
          <div className="ts-empty">
            <div className="ts-empty-title">No teams yet</div>
            <div className="ts-empty-sub">
              Teams decide which orders each person sees. Start with the way work
              already splits — production, sales, delivery.
            </div>
            <div className="ts-empty-actions">
              <button className="ts-btn-primary" onClick={handleOpenCreate}>
                + Create the first team
              </button>
            </div>
          </div>
        )}

        {/* team cards */}
        {!loading && filteredTeams.map(renderTeamCard)}
      </div>

      {/* ---- membership slide-over ---- */}
      {managingTeam && (
        <>
          <div className="ts-overlay" onClick={() => setManagingId(null)} />
          <div className="ts-slideover">
            <div className="ts-so-header">
              <div>
                <div className="ts-so-title">Manage {managingTeam.name}</div>
                <div className="ts-so-sub">
                  {members.length} members · {managingTeam.ordersCount ?? 0} orders
                </div>
              </div>
              <button className="ts-so-close" onClick={() => setManagingId(null)}>
                ✕
              </button>
            </div>

            <div className="ts-so-body">
              {/* current members */}
              <div className="ts-so-section-label">
                <div className="ts-so-label">
                  <span>Current members</span>
                  <span className="ts-so-count">{members.length}</span>
                </div>
                <span className="ts-so-hint">No remove — only move</span>
              </div>
              {members.map((m) => {
                const move = moveFor(m.id);
                const leaving = move && move.fromTeamId === managingTeam.id;
                return (
                  <div
                    key={m.id}
                    className={`ts-member-row ${leaving ? "ts-member-row--muted" : ""}`}
                    style={{ position: "relative" }}
                  >
                    <span className="ts-member-avatar ts-member-avatar--accent">
                      {initialsOf(m)}
                    </span>
                    <span className={`ts-member-name ${leaving ? "ts-member-name--struck" : ""}`}>
                      {fullName(m)}
                    </span>
                    <span className={`ts-badge ${roleLabel(m.role) === "SUDO" ? "ts-badge--sudo" : ""}`}>
                      {roleLabel(m.role)}
                    </span>
                    {leaving ? (
                      <>
                        <span className="ts-badge ts-badge--leaving">leaving → {move.toName}</span>
                        <span className="ts-undo" onClick={() => removeMove(m.id)}>
                          Undo
                        </span>
                      </>
                    ) : (
                      <button
                        className={`ts-move-btn ${moveMenuFor === m.id ? "ts-move-btn--active" : ""}`}
                        onClick={() => setMoveMenuFor(moveMenuFor === m.id ? null : m.id)}
                      >
                        Move to<span style={{ fontSize: 8 }}>▾</span>
                      </button>
                    )}
                    {moveMenuFor === m.id && (
                      <div className="ts-move-menu">
                        <div className="ts-move-menu-label">Move {m.firstname} to</div>
                        {teams
                          .filter((t) => t.id !== managingTeam.id)
                          .map((t) => (
                            <div
                              key={t.id}
                              className={`ts-move-menu-item ${isUnassigned(t) ? "ts-move-menu-item--divider" : ""}`}
                              onClick={() => handleMoveMember(m, t)}
                            >
                              {t.name}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* add people */}
              <div style={{ margin: "14px 18px 0", height: 1, background: "var(--border)" }} />
              <div className="ts-so-section-label">
                <div className="ts-so-label">
                  <span>Add people</span>
                </div>
                <div className="ts-add-tabs">
                  <button
                    className={`ts-add-tab ${addTab === "everyone" ? "ts-add-tab--active" : ""}`}
                    onClick={() => setAddTab("everyone")}
                  >
                    Everyone
                  </button>
                  <button
                    className={`ts-add-tab ${addTab === "unassigned" ? "ts-add-tab--active" : ""}`}
                    onClick={() => setAddTab("unassigned")}
                  >
                    Unassigned only
                    <span className="ts-add-tab-warn">{unassignedCount}</span>
                  </button>
                </div>
              </div>
              <div style={{ padding: "0 18px 10px" }}>
                <div className="ts-search">
                  <span className="ts-filter-icon">⌕</span>
                  <input
                    placeholder={`Search all ${assignableUsers.length} staff`}
                    value={addSearch}
                    onChange={(e) => setAddSearch(e.target.value)}
                  />
                </div>
              </div>

              {addablePeople.map((u) => {
                const selected = !!moveFor(u.id);
                const currentTeam = teams.find((t) => t.id === u.team_id);
                return (
                  <div key={u.id}>
                    <div
                      className={`ts-add-row ${selected ? "ts-add-row--selected" : ""}`}
                      onClick={() => toggleAddPerson(u)}
                    >
                      <span className={`ts-check ${selected ? "ts-check--on" : ""}`}>
                        {selected ? "✓" : ""}
                      </span>
                      <span className="ts-member-avatar">{initialsOf(u)}</span>
                      <span className="ts-member-name" style={{ flex: "none" }}>
                        {fullName(u)}
                      </span>
                      {currentTeam ? (
                        <span className={`ts-badge ${isUnassigned(currentTeam) ? "ts-badge--leaving" : ""}`}>
                          {currentTeam.name}
                        </span>
                      ) : (
                        <span className="ts-badge ts-badge--leaving">Unassigned</span>
                      )}
                      <span className="ts-badge ts-badge--muted" style={{ marginLeft: "auto" }}>
                        {roleLabel(u.role)}
                      </span>
                    </div>
                    {selected && currentTeam && !isUnassigned(currentTeam) && (
                      <div className="ts-add-warning">
                        <span>⚠</span> Leaves {currentTeam.name} — a person can only be on one team.
                      </div>
                    )}
                  </div>
                );
              })}

              {/* already-on-team (dimmed) */}
              {assignableUsers
                .filter((u) => members.some((m) => m.id === u.id))
                .filter(
                  (u) =>
                    !addSearch.trim() ||
                    fullName(u).toLowerCase().includes(addSearch.trim().toLowerCase())
                )
                .map((u) => (
                  <div key={u.id} className="ts-add-row ts-add-row--disabled">
                    <span className="ts-check" />
                    <span className="ts-member-avatar">{initialsOf(u)}</span>
                    <span className="ts-member-name">{fullName(u)}</span>
                    <span className="ts-badge ts-badge--muted">Already on this team</span>
                  </div>
                ))}
            </div>

            {/* footer */}
            <div className="ts-so-footer">
              {pendingMoves.length > 0 && (
                <div className="ts-pending">
                  <div className="ts-pending-head">
                    <span className="ts-pending-label">Pending changes</span>
                    <span className="ts-pending-count">{pendingMoves.length}</span>
                  </div>
                  <div className="ts-pending-list">
                    {pendingMoves.map((m) => (
                      <div key={m.userId}>
                        <span className="ts-pending-arrow">→</span> {m.userName} moves from{" "}
                        <span style={{ color: "var(--muted)" }}>{m.fromName}</span> →{" "}
                        <span style={{ fontWeight: 600 }}>{m.toName}</span>
                      </div>
                    ))}
                  </div>
                  <div className="ts-pending-note">
                    Team ends with{" "}
                    {members.length -
                      pendingMoves.filter((m) => m.fromTeamId === managingTeam.id).length +
                      pendingMoves.filter((m) => m.toTeamId === managingTeam.id).length}{" "}
                    members. Order visibility changes immediately on save.
                  </div>
                </div>
              )}
              <div className="ts-so-actions">
                <div className="ts-kbd-group">
                  <span className="ts-kbd">Esc close</span>
                  <span className="ts-kbd">⌘↵ save</span>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="ts-btn-ghost" onClick={() => setManagingId(null)}>
                    Cancel
                  </button>
                  <button
                    className="ts-btn-save"
                    onClick={saveMembers}
                    disabled={pendingMoves.length === 0 || savingMembers}
                    style={{ opacity: pendingMoves.length === 0 ? 0.5 : 1 }}
                  >
                    {savingMembers
                      ? "Saving…"
                      : pendingMoves.length > 0
                      ? `Save ${pendingMoves.length} change${pendingMoves.length !== 1 ? "s" : ""}`
                      : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TeamSettings;
